import { useMemo, useState } from "react";
import { useOrg } from "../../lib/org/useOrg";
import { useAuth } from "../../lib/auth/useAuth";
import type { OrgRole } from "../../lib/org/types";
import { RangePicker, type RangePreset } from "./reports/shared";
import { CashFlow } from "./reports/CashFlow";
import { SpendByCategory } from "./reports/SpendByCategory";
import { TopVendors } from "./reports/TopVendors";
import { MonthlyTrend } from "./reports/MonthlyTrend";
import { InvoicesSummary } from "./reports/InvoicesSummary";
import { ApprovalAndTax } from "./reports/ApprovalAndTax";
import { KpiRow } from "./reports/KpiRow";
import { CategoryTrend } from "./reports/CategoryTrend";
import { VendorConcentration } from "./reports/VendorConcentration";
import { ExpenseDistribution } from "./reports/ExpenseDistribution";
import { ScopePinnedBadge } from "../components/ScopePinnedBadge";
import { useCapabilities } from "../../lib/permissions/useCapabilities";
import { useEffectiveScopeForTab } from "../../lib/scope";

interface VisibleSections {
  // Cross-org headline. Hidden from employees because revenue is not their
  // scope; every other role (including auditor, read-only) sees it.
  cashFlow: boolean;
  spendByCategory: boolean;
  monthlyTrend: boolean;
  topVendors: boolean;
  invoicesSummary: boolean;
  approvalAndTax: boolean;
  // When true, category + trend are scoped to the current user's submissions
  // (so an employee only sees their own spending).
  scopeToSelf: boolean;
}

function resolveSections(role: OrgRole | null): VisibleSections {
  switch (role) {
    case "employee":
      return {
        cashFlow: false,
        spendByCategory: true,
        monthlyTrend: true,
        topVendors: false,
        invoicesSummary: false,
        approvalAndTax: false,
        scopeToSelf: true,
      };
    case "auditor":
      return {
        cashFlow: true,
        spendByCategory: true,
        monthlyTrend: true,
        topVendors: true,
        invoicesSummary: true,
        approvalAndTax: true,
        scopeToSelf: false,
      };
    // owner / admin / accountant — and the null fallback — see everything.
    default:
      return {
        cashFlow: true,
        spendByCategory: true,
        monthlyTrend: true,
        topVendors: true,
        invoicesSummary: true,
        approvalAndTax: true,
        scopeToSelf: false,
      };
  }
}

export function Reports() {
  const [preset, setPreset] = useState<RangePreset>("ytd");
  const { activeRole } = useOrg();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { scope, pinned } = useEffectiveScopeForTab("reports");

  const hasReadReports = can("read:reports");
  const visible = useMemo(() => resolveSections(activeRole), [activeRole]);

  // Wave 22 — self-filter is now scope-driven. In "mine" every sub-report
  // filters to the current user's submissions; in "company" they see the
  // full org set. The legacy role-based `scopeToSelf` stays in place so
  // employees (who are locked to "mine" anyway) still get the tightened
  // section list.
  const selfFilter = scope === "mine" ? user?.id ?? null : null;

  if (!hasReadReports) {
    return (
      <div
        role="alert"
        data-testid="page-access-denied"
        className="max-w-md mx-auto mt-12 bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center"
      >
        <h1 className="text-lg font-semibold text-slate-900">
          You don't have access to Reports
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Ask an admin to grant you the <code>read:reports</code> capability.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-scope={scope} data-testid="reports-root">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-slate-900">Reports</h1>
            {pinned && <ScopePinnedBadge forcedScope={scope} />}
          </div>
          <p className="text-sm text-slate-500 mt-0.5">
            {scope === "mine"
              ? "Your spending activity."
              : "Spend analytics across expenses and invoices."}
          </p>
        </div>
        <RangePicker value={preset} onChange={setPreset} />
      </div>

      {/* Cash flow spans the full width — it's the headline for the page. */}
      {visible.cashFlow && <CashFlow preset={preset} submittedBy={selfFilter} />}

      {/* KPI row sits below CashFlow — burn rate, net margin, receipt
          compliance. Employees see only burn rate (revenue + compliance are
          org-wide metrics). */}
      <KpiRow preset={preset} role={activeRole} submittedBy={selfFilter} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {visible.spendByCategory && (
          <SpendByCategory preset={preset} submittedBy={selfFilter} />
        )}
        {visible.topVendors && <TopVendors preset={preset} />}
        {visible.monthlyTrend && <MonthlyTrend submittedBy={selfFilter} />}
        {visible.invoicesSummary && <InvoicesSummary preset={preset} />}
        {visible.approvalAndTax && (
          <ApprovalAndTax preset={preset} submittedBy={selfFilter} />
        )}
        <CategoryTrend submittedBy={selfFilter} />
        {visible.topVendors && (
          <VendorConcentration preset={preset} submittedBy={selfFilter} />
        )}
        <ExpenseDistribution preset={preset} submittedBy={selfFilter} />
      </div>
    </div>
  );
}
